/*! Based on gettext.js by Guillaume Potier - MIT Licensed */

/*
$gettext(msgid) Translates a string.
$ngettext(msgid, msgid_plural, n) Translates a pluralizable string.
$pgettext(msgctxt, msgid) Translates a string specified by context.
$pngettext(msgctxt, msgid, msgid_plural, n) Translates a potentially pluralizable string, potentially specified by context.

All function accept an object as the last argument, with variables to interpolate.
E.g. $gettext('I ate %{count} apples in %{days} days.', { count: 10, days: 2 }) => 'I ate 10 apples in 2 days.'
*/

const I18n = function (options) {
  options = options || {}

  // default values that could be overriden in i18n() construct
  const defaults = {
    locale: (typeof document !== 'undefined' ? document.documentElement.getAttribute('lang') : false) || 'en',
    pluralFunc: function (n) {
      return { nplurals: 2, plural: n !== 1 ? 1 : 0 }
    }
  }

  // handy mixins taken from underscode.js
  const _ = {
    isObject: function (obj) {
      const type = typeof obj
      return type === 'function' || (type === 'object' && !!obj)
    }
  }

  const _pluralFuncs = {}
  const _pluralForms = {}
  let _locale = options.locale || defaults.locale
  const _dictionary = {}

  if (options.messages) {
    _dictionary[_locale] = options.messages
  }

  if (options.pluralForms) {
    _pluralForms[_locale] = options.pluralForms
  }

  // eg: strfmt('I ate %{count} apples in %{days} days.', { count: 10, days: 2 }) => 'I ate 10 apples in 2 days.'
  const strfmt = function (msgid, args) {
    const regularExpression = /%\{([A-z0-9_]+)}/g
    const matches = [...msgid.matchAll(regularExpression)]

    return matches.reduce((acc, match) => {
      const interpolationVariable = match[1]
      acc = acc.replace(`%{${interpolationVariable}}`, args[interpolationVariable])
      return acc
    }, msgid)
  }

  const getPluralFunc = function (pluralForm) {
    // Plural form string regexp
    // taken from https://github.com/Orange-OpenSource/gettext.js/blob/master/lib.gettext.js
    // plural forms list available here http://localization-guide.readthedocs.org/en/latest/l10n/pluralforms.html
    // eslint-disable-next-line prefer-regex-literals
    const pluralRegex = new RegExp(
      '^\\s*nplurals\\s*=\\s*[0-9]+\\s*;\\s*plural\\s*=\\s*(?:\\s|[-\\?\\|&=!<>+*/%:;n0-9_()])+'
    )

    const match = pluralForm.match(pluralRegex)

    if (!match || match[0] !== pluralForm) throw new Error(`The plural form ${pluralForm} is not valid`)

    // Careful here, this is a hidden eval() equivalent..
    // Risk should be reasonable though since we test the pluralForm through regex before
    // taken from https://github.com/Orange-OpenSource/gettext.js/blob/master/lib.gettext.js
    // eslint-disable-next-line no-new-func
    return new Function(
      'n',
      'let plural, nplurals; ' +
        pluralForm +
        ' return { nplurals: nplurals, plural: (plural === true ? 1 : (plural ? plural : 0)) };'
    )
  }

  // Proper translation function that handle plurals and directives
  // Contains juicy parts of https://github.com/Orange-OpenSource/gettext.js/blob/master/lib.gettext.js
  const translate = function (messages, n, options, extra) {
    // Singular is very easy, just pass dictionary message through strfmt
    if (!options.plural_form) return strfmt(messages[0], extra)

    let plural

    // if a plural func is given, use that one
    if (options.pluralFunc) {
      plural = options.pluralFunc(n)

      // if plural form never interpreted before, do it now and store it
    } else if (!_pluralFuncs[_locale]) {
      _pluralFuncs[_locale] = getPluralFunc(_pluralForms[_locale])
      plural = _pluralFuncs[_locale](n)

      // we have the plural function, compute the plural result
    } else {
      plural = _pluralFuncs[_locale](n)
    }

    // If there is a problem with plurals, fallback to singular one
    if (typeof plural.plural === 'undefined' || plural.plural > plural.nplurals || messages.length <= plural.plural) {
      plural.plural = 0
    }

    return strfmt(messages[plural.plural], extra)
  }

  return {
    strfmt,
    setMessages: function (locale, messages, pluralForms) {
      if (!locale || !messages) throw new Error('You must provide a locale and messages')

      if (typeof locale !== 'string' || !_.isObject(messages)) {
        throw new Error('Invalid arguments')
      }

      if (pluralForms) {
        _pluralForms[locale] = pluralForms
      }

      _dictionary[locale] = messages

      return this
    },
    setLocale: function (locale) {
      _locale = locale
      return this
    },
    $gettext: function (msgid, extra) {
      return this.$pngettext(undefined, msgid, undefined, undefined, extra)
    },
    $ngettext: function (msgid, msgidPlural, n, extra) {
      return this.$pngettext(undefined, msgid, msgidPlural, n, extra)
    },
    $pgettext: function (msgctxt, msgid, extra) {
      return this.$pngettext(msgctxt, msgid, undefined, undefined, extra)
    },
    $pngettext: function (msgctxt, msgid, msgidPlural, n, extra) {
      if (typeof msgid !== 'string') {
        throw new Error(this.strfmt(`Msgid ${msgid} is not a valid translatable string`, msgid))
      }

      const options = { plural_form: false }
      let exist
      let translation

      exist = _dictionary && _dictionary[_locale] && _dictionary[_locale][msgid]

      // For gettext with plural, msgid will be an array.
      // For gettext with context, msgid will be an object.
      // We check if there are any matches. If not, we just return the untranslated msgid.
      if (msgidPlural) {
        exist = exist && Array.isArray(_dictionary[_locale][msgid])
      } else if (msgctxt) {
        exist = exist && typeof _dictionary[_locale][msgid] === 'object'
      } else {
        exist = exist && typeof _dictionary[_locale][msgid] === 'string'
      }

      if (!exist) {
        translation = msgid
        options.pluralFunc = defaults.pluralFunc
      } else {
        if (msgctxt) {
          translation = _dictionary[_locale][msgid][msgctxt]
        } else {
          translation = _dictionary[_locale][msgid]
        }
      }

      // Singular form
      if (!msgidPlural) {
        return translate([translation], n, options, extra)
      }

      // Plural one
      options.plural_form = true
      return translate(exist ? translation : [msgid, msgidPlural], n, options, extra)
    }
  }
}

export default I18n
